import { observe } from "selector-observer";

// Nav pills filter the cards by nav position. Each pill carries the single position it
// selects; each card and section carries every position it belongs to, space separated.
// Only one pill can be pressed at a time, and no pill pressed means no filter, so
// everything shows.
const BLOCK = ".expandableinfocards";
const PILL_ROW = ".expandableinfocards__filters";
const PILL = ".expandableinfocards__pill";
const PRESSED = `${PILL}[aria-pressed="true"]`;
const GRID = ".expandableinfocards__grid";
const CARD_BODY = ".expandableinfocards__card-body";

// The cards are start-aligned so that opening one never drags its neighbours' height with
// it - which also means the grid stopped evening a row out. The floor is measured here
// instead: the tallest card body in a grid becomes a min-height on all of them, so a row
// is level at rest whatever the content length.
//
// The body deliberately excludes the disclosure, which is the only part that changes
// height. Levelling the whole card instead would leave slack for the CTA's margin-top:auto
// to absorb, and opening the disclosure would eat that slack - growing the block upwards
// before the card grew at all. Measuring only the fixed region keeps expansion downwards.
//
// Desktop only; under xl the grid is a single column, where a shared height is just dead
// space under the short cards.
const DESKTOP = window.matchMedia("(min-width: 1200px)");

// observe() rather than DOMContentLoaded so the pills still work after Optimizely
// re-renders a block in on-page edit mode.
observe(PILL_ROW, {
  add(pillRow) {
    pillRow.addEventListener("click", (event) => {
      const pill = event.target.closest(PILL);
      const block = pillRow.closest(BLOCK);

      if (!pill || !block) {
        return;
      }

      // The filters are mutually exclusive: a card list showing two navs at once reads as
      // though every card belongs to both. So everything is released before the clicked
      // pill is set, and pressing the pressed pill clears it back to the unfiltered view.
      const pressed = pill.getAttribute("aria-pressed") === "true";

      block.querySelectorAll(PILL).forEach((other) => {
        other.setAttribute("aria-pressed", "false");
      });

      pill.setAttribute("aria-pressed", String(!pressed));

      applyFilter(block);
    });
  },
});

observe(GRID, {
  add(grid) {
    levelCards(grid);

    // Web fonts land after the first paint and change how the copy wraps, so the floor is
    // taken again once they are in.
    document.fonts?.ready?.then(() => levelCards(grid));

    // A resize rewraps the copy too, and crossing the xl boundary turns the floor on or
    // off entirely. Coalesced into a frame so a drag does not remeasure per event.
    let frame = 0;
    grid.eicRelevel = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => levelCards(grid));
    };

    window.addEventListener("resize", grid.eicRelevel);
  },

  remove(grid) {
    window.removeEventListener("resize", grid.eicRelevel);
  },
});

function levelCards(grid) {
  const bodies = Array.from(grid.querySelectorAll(CARD_BODY));

  if (bodies.length === 0) {
    return;
  }

  // Clearing first matters on every pass: a body measured through its own floor would
  // only ever report the previous measurement back.
  bodies.forEach((body) => {
    body.style.minHeight = "";
  });

  if (!DESKTOP.matches) {
    return;
  }

  // Filtered-out cards are display:none and measure 0, so a filtered row levels against
  // the cards actually on screen. An open disclosure cannot skew this - it lives outside
  // the body being measured.
  const tallest = Math.max(...bodies.map((body) => body.offsetHeight));

  bodies.forEach((body) => {
    body.style.minHeight = `${tallest}px`;
  });
}

function applyFilter(block) {
  // Read the state back off the DOM rather than tracking it separately, so the pills stay
  // the single source of truth even if one is toggled from elsewhere.
  const active = Array.from(block.querySelectorAll(PRESSED), (pill) => pill.dataset.eicNav);

  // Cards carry data-eic-filter, sections carry data-eic-sections - a section hides along
  // with its heading once none of its cards match. [hidden] is enforced in the SCSS,
  // because the cards' own display:flex outranks the browser's rule for the attribute.
  block
    .querySelectorAll("[data-eic-filter], [data-eic-sections]")
    .forEach((element) => {
      const positions = (
        element.dataset.eicFilter ?? element.dataset.eicSections
      ).split(" ");

      // Nothing pressed shows everything. Otherwise keep whatever matches the pressed
      // pill. The component drops untagged cards before render, so an empty attribute
      // should not reach here - it still reads as "matches nothing" if one ever does.
      element.hidden =
        active?.length > 0 &&
        !active?.some((position) => positions?.includes(position));
    });

  // Filtering changes which cards a row has to level against, so the floor is retaken
  // once the new set is in place.
  block.querySelectorAll(GRID).forEach((grid) => levelCards(grid));
}
